import { logger } from '@/utils/logger';
import { fetchCSRFToken } from '@/lib/crm/sugarCrm';

/**
 * FCA Lookup Service
 * Handles FCA number validation and company data retrieval
 */
export class FcaLookupService {
  /**
   * Perform FCA lookup for a given FCA ID
   */
  static async lookupFcaData(fcaId) {
    if (!fcaId?.trim()) {
      throw new Error('FCA ID is required');
    }

    try {
      const csrfToken = await fetchCSRFToken();

      const response = await fetch('/api/fca-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(fcaId),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: Failed to fetch FCA data`);
      }

      const data = await response.json();

      if (data === 'error' || data.address?.Data === null) {
        logger.validation('fcaNumber', 'Invalid FCA number format', fcaId);
        return { success: false, error: 'Invalid FCA number' };
      }

      return { success: true, data: this.transformFcaData(data) };
    } catch (error) {
      logger.error('FCA lookup service error', { fcaId, type: error.constructor.name });
      
      return { success: false, error: 'FCA lookup service unavailable' };
    }
  }

  /**
   * Transform raw FCA API response to usable format
   */
  static transformFcaData(data) {
    const companyData = (data.company && Array.isArray(data.company.Data) && data.company.Data[0])
      ? data.company.Data[0]
      : {};

    const addressData = (data.address && Array.isArray(data.address.Data) && data.address.Data[0])
      ? data.address.Data[0]
      : {};

    return {
      companyStatus: companyData.Status || '',
      name: companyData['Organisation Name'] || '',
      phone: addressData['Phone Number'] || '',
      postcode: addressData.Postcode || '',
      al1: addressData['Address Line 1'] || '',
      al2: addressData['Address Line 2'] || '',
      town: addressData.Town || '',
      county: addressData.County || '',
    };
  }

  /**
   * Validate FCA ID format
   */
  static validateFcaId(fcaId) {
    if (!fcaId || typeof fcaId !== 'string') {
      return false;
    }

    // Remove any non-numeric characters
    const numericId = fcaId.replace(/[^\d]/g, '');

    // FCA IDs are typically 6 digits, minimum 100000
    return numericId.length >= 6 && parseInt(numericId) >= 100000;
  }

  /**
   * Format FCA ID input (remove non-numeric characters)
   */
  static formatFcaId(value) {
    return value.replace(/[^\d,]/g, '');
  }
}

export const fcaLookupService = new FcaLookupService();
